import m from "mithril";
import i18n from "../../../shared/i18n/i18n";
import Auth from "../../../shared/auth";
import FormUtilities from "../../../shared/utilities/form_utilities";
import TextUtilities from "../../../shared/utilities/text_utilities";
import FormElementsMenu from "./FormElementsMenu";
import TextEditor from "../../../shared/components/component_text_editor";
import OptionsEditor from "../shared/OptionsEditor";
import UndoHistory from "../shared/undo_history";
import { fieldHasOptions } from "../../../shared/utilities/assessment_dependencies";

let fieldCopy = null;
let originalField = null;
let isNewField = false;
let optionInput = "";
let optionsForDeps = [];

// True while the field editor is on screen. Its edits live in fieldCopy and only
// reach the unit on "save field", so the page disables the checklist save button
// while the toolbox is open rather than writing out a unit without them.
let isMounted = false;

export function isOpen() {
    return isMounted;
}

// The toolbox lives on a mount root of its own inside the editor's tree, so
// tearing that tree down (switching tabs) drops the node without running
// onremove. The editor calls this as it remounts, where the elements menu is
// what gets rendered.
export function markClosed() {
    isMounted = false;
}

// Edits are made against fieldCopy and only reach the unit on save_field, so
// compare the copy with the field as it was opened. A field dragged in from the
// toolbox is not part of the saved checklist until it is saved, even if
// untouched.
export function hasUnsavedChanges() {
    if (fieldCopy === null) {
        return false;
    }

    return isNewField || JSON.stringify(fieldCopy) !== originalField;
}

// The toolbox keeps its state in module scope, so it outlives the mount. Callers
// that take the pending edits away must clear it, or hasUnsavedChanges keeps
// reporting edits that are already gone. Re-baselines rather than dropping
// fieldCopy: the toolbox may still be mounted and its view reads it unguarded.
export function discardUnsavedChanges() {
    originalField = fieldCopy === null ? null : JSON.stringify(fieldCopy);
    isNewField = false;
}

function revertToolbox() {
    // Saved, deleted or cancelled: nothing is pending once the toolbox is gone.
    discardUnsavedChanges();
    m.mount(document.querySelector(".form-toolbox"), FormElementsMenu);
}

function saveUndoHistory() {
    UndoHistory.snapshot(undoHistory, unit);
}


function isNameValid() {
    return TextUtilities.hasVisibleText(TextUtilities.safeDecodeURIComponent(fieldCopy.name, true));
}

function saveField() {
    if (FormUtilities.validateFields(".field-edit input") && isNameValid()) {
        saveUndoHistory();
        for (let i = 0; i < unit.sections.length; i++) {
            for (let x = 0; x < unit.sections[i].fields.length; x++) {
                if (unit.sections[i].fields[x].id === fieldCopy.id) {
                    unit.sections[i].fields[x] = fieldCopy;
                }
            }
        }
        isNewField = false;
        revertToolbox();
        m.redraw();
    }
}

function removeField() {
    for (let i = 0; i < unit.sections.length; i++) {
        for (let x = 0; x < unit.sections[i].fields.length; x++) {
            if (unit.sections[i].fields[x].id === fieldCopy.id) {
                saveUndoHistory();
                unit.sections[i].fields.splice(x, 1);
            }
        }
    }
    revertToolbox();
    m.redraw();
}

function checkFieldOps(opId) {
    for (let i = 0; i < fieldCopy.prerequisiteOptionIds.length; i++) {
        if (fieldCopy.prerequisiteOptionIds[i] === opId) {
            return true;
        }
    }

    return false;
}

function updateFieldOptionDeps(options) {
    fieldCopy.prerequisiteOptionIds = [];
    for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
            fieldCopy.prerequisiteOptionIds.push(options[i].value);
        }
    }
}

function getFieldOptions(fieldId) {
    let options = [];
    for (let i = 0; i < unit.sections.length; i++) {
        for (let v = 0; v < unit.sections[i].fields.length; v++) {
            if (unit.sections[i].fields[v].id === fieldId) {
                return options;
            }

            if (fieldHasOptions(unit.sections[i].fields[v].type)) {
                for (let x = 0; x < unit.sections[i].fields[v].options.length; x++) {
                    options.push(unit.sections[i].fields[v].options[x]);
                }
            }
        }
    }

    return options;
}

function option(id, value) {
    this.id = id || Math.random().toString(36).slice(2);
    this.value = value || "";
}

function addFieldOption(field, value) {
    let match = false;
    for (let i = 0; i < field.options.length; i++) {
        if (field.options[i].value === value) {
            match = true;
        }
    }

    if (!match) {
        field.options.push(new option(null, value));
    }
}

function removeFieldOption(field, id) {
    for (let i = 0; i < field.options.length; i++) {
        if (field.options[i].id === id) {
            field.options.splice(i, 1);
        }
    }
}

function setDropDownSelectionMode(mode) {
    // The elements menu only has a single "dropdown" type, so a newly added field
    // defaults to dropdownsingle
    if (mode === "dropdown") {
        mode = "dropdownsingle";
    }
    if (mode === "dropdownsingle" || mode === "dropdownmulti") {
        fieldCopy.type = mode;
    }
}

function showPlaceholder() {
    return fieldCopy.type === 'textfield'
        || fieldCopy.type === 'textbox'
        || fieldCopy.type === 'number';
}

function showOptions() {
    return fieldHasOptions(fieldCopy.type);
}

function showDependencies() {
    return optionsForDeps.length > 0;
}

let unit;
let undoHistory;

const EditFieldToolbox = {
    oninit: function (vnode) {
        unit = vnode.attrs.unit;
        undoHistory = vnode.attrs.undoHistory;
        fieldCopy = JSON.parse(JSON.stringify(vnode.attrs.fld));
        setDropDownSelectionMode(fieldCopy.type);
        // Baseline after the type is normalised, so opening a legacy "dropdown"
        // field does not look edited straight away.
        originalField = JSON.stringify(fieldCopy);
        isNewField = !!vnode.attrs.isNew;
        optionsForDeps = getFieldOptions(fieldCopy.id);
        isMounted = true;
    },
    onremove: function () {
        isMounted = false;
    },
    view: function () {
        return [
            m(".form-toolbox", [
                m("h4.mb-2", [i18n.t("edit_field"), " - ", i18n.t(fieldCopy.type)]),
                fieldCopy ? [
                    m(".form.full-width.field-edit", [
                        m(".form-section.required", [
                            m(".form-label", i18n.t("name")),
                            m(TextEditor, {
                                content: encodeURIComponent(fieldCopy.name || ""),
                                simpleToolbar: true,
                                onchange(content) {
                                    fieldCopy.name = content;
                                }
                            }),
                            !isNameValid() ? m(".text-error.padding", { style: "font-size: 0.75rem;" }, i18n.t("complete_required_fields")) : ""
                        ]),
                        showPlaceholder() ? [
                            m(".form-section", [
                                m(".form-label", i18n.t("placeholder")),
                                m("textarea", {
                                    oninput: function () {
                                        fieldCopy.information = this.value;
                                    },
                                    value: fieldCopy.information
                                })
                            ])
                        ] : "",
                        m(".form-section.checkbox-section", [
                            m("div", {
                                onclick: function () {
                                    fieldCopy.isRequired = !fieldCopy.isRequired;
                                }
                            }, [
                                fieldCopy.isRequired ? m("i.icon-checkbox-checked") : m("i.icon-checkbox-empty.text-gray"),
                                m("span.ml-1", i18n.t("required_field"))
                            ])
                        ]),
                        fieldCopy.type.includes("dropdown") ? m(".form-section", [
                            m(".form-label", i18n.t("selection_mode")),
                            m(".form-buttons", [
                                m("button", {class: fieldCopy.type === "dropdownsingle" ? "btn-cta" : "", onclick: () => setDropDownSelectionMode("dropdownsingle")}, i18n.t("single_select")),
                                m("button", {class: fieldCopy.type === "dropdownmulti" ? "btn-cta" : "", onclick: () => setDropDownSelectionMode("dropdownmulti")}, i18n.t("multi_select"))
                            ])
                        ]) : "",
                        showOptions() ? m(OptionsEditor, {
                            options: fieldCopy.options,
                            optionInput: optionInput,
                            onInput: function (val) { optionInput = val; },
                            onAdd: function () { addFieldOption(fieldCopy, optionInput); optionInput = ""; },
                            onRemove: function (id) { removeFieldOption(fieldCopy, id); },
                            onSort: function (evt) {
                                const item = fieldCopy.options.splice(evt.oldIndex, 1)[0];
                                fieldCopy.options.splice(evt.newIndex, 0, item);
                                m.redraw();
                            }
                        }) : "",
                        showDependencies() ? [
                            m(".form-section", [
                                m(".form-label", i18n.t("dependencies")),
                                m("select[multiple].section-option-deps-select", {
                                    onchange: function () {
                                        updateFieldOptionDeps(this.options);
                                    }
                                }, optionsForDeps.map(function (op) {
                                    return [
                                        m("option", {
                                            value: op.id,
                                            oncreate: function (e) {
                                                if (checkFieldOps(op.id)) {
                                                    e.dom.setAttribute("selected", "selected");
                                                }
                                            }
                                        }, op.value)
                                    ];
                                }))
                            ])
                        ] : "",
                        m(".form-buttons", [
                            Auth.validateRights(["unit_form_edit"], [
                                m("button.btn-error.flex-float-left", {onclick: removeField}, i18n.t("delete_field")),
                                m("button", {onclick: saveField}, i18n.t("save_field"))
                            ]),
                            m("button.ml-1", {onclick: revertToolbox}, i18n.t("cancel"))
                        ]),
                        hasUnsavedChanges() ? m(".text-error.my-2", { style: "text-align: right;" }, [
                            m("i.icon-warning"), m("span.ml-2", i18n.t("unsaved_changes"))
                        ]) : ""
                    ])
                ] : m("button.ml-1", {onclick: revertToolbox}, i18n.t("cancel"))
            ])
        ];
    }
};

export default EditFieldToolbox;
